import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getCurrentWarranty, getMySubmittedWarrantyQuotes, getWarrantyBilling } from "@/Calls/callWarrantyAuth";
import { Card } from "@/components/ui/card";
import { FileSearch, FileText, Receipt, Loader2, ShieldCheck } from "lucide-react";

interface Company {
    company_name: string;
    company_code: string;
    status: string;
    lead_rate: string;
    billing_invoice_no: number | null;
}

export default function WarrantyDashboard() {
    const [company,  setCompany]  = useState<Company | null>(null);
    const [quotes,   setQuotes]   = useState<any[]>([]);
    const [billing,  setBilling]  = useState<{ charges: any[]; total: number } | null>(null);
    const [loading,  setLoading]  = useState(true);

    useEffect(() => {
        Promise.all([
            getCurrentWarranty(),
            getMySubmittedWarrantyQuotes(),
            getWarrantyBilling(),
        ]).then(([me, q, b]) => {
            setCompany(me.data.company);
            setQuotes(q.data.quotes ?? []);
            setBilling({ charges: b.data.charges ?? [], total: b.data.total ?? 0 });
        }).finally(() => setLoading(false));
    }, []);

    if (loading) return (
        <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
    );

    const pendingQuotes   = quotes.filter((q) => q.status === "PENDING").length;
    const acceptedQuotes  = quotes.filter((q) => q.status === "ACCEPTED").length;
    const leadsPulledMTD  = billing?.charges.filter((c) => {
        const d = new Date(c.charges_created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    }).length ?? 0;

    const stats = [
        { label: "Leads Pulled MTD", value: leadsPulledMTD, icon: FileSearch,  color: "text-purple-600", to: "/warranty/quote-requests" },
        { label: "Quotes Submitted",  value: quotes.length,  icon: FileText,    color: "text-blue-600",   to: "/warranty/my-quotes"      },
        { label: "Pending Review",    value: pendingQuotes,  icon: Receipt,     color: "text-orange-500", to: "/warranty/my-quotes"      },
        { label: "Accepted Policies", value: acceptedQuotes, icon: ShieldCheck, color: "text-green-600",  to: "/warranty/my-quotes"      },
    ];

    return (
        <div className="max-w-5xl mx-auto space-y-6">
            <div>
                <h1 className="text-2xl font-light uppercase tracking-wide">
                    {company?.company_name ?? "Warranty Dashboard"}
                </h1>
                <p className="text-muted-foreground text-sm mt-1">
                    {company?.company_code} · Lead rate ${company?.lead_rate}/lead
                </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {stats.map(({ label, value, icon: Icon, color, to }) => (
                    <Link key={label} to={to}>
                        <Card className="p-5 hover:shadow-md transition-shadow cursor-pointer">
                            <Icon className={`h-6 w-6 mb-3 ${color}`} />
                            <p className="text-2xl font-bold">{value}</p>
                            <p className="text-xs text-muted-foreground mt-1">{label}</p>
                        </Card>
                    </Link>
                ))}
            </div>

            {billing && billing.charges.length > 0 && (
                <Card className="p-5">
                    <div className="flex justify-between items-center mb-3">
                        <h2 className="font-semibold">Recent Lead Charges</h2>
                        <Link to="/warranty/billing" className="text-xs text-purple-600 hover:underline">View All</Link>
                    </div>
                    <div className="divide-y text-sm">
                        {billing.charges.slice(0, 5).map((c) => (
                            <div key={c.charges_id} className="py-2 flex justify-between">
                                <span className="text-muted-foreground">{c.charges_fee_description}</span>
                                <span className="font-mono font-medium">${Number(c.charges_amount).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 flex justify-between font-semibold border-t pt-2">
                        <span>Total Balance</span>
                        <span>${Number(billing.total).toFixed(2)}</span>
                    </div>
                </Card>
            )}
        </div>
    );
}
